import { useCallback, useEffect, useState } from "react";
import { FaEdit, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import SWComponentFormModal from "../components/SWComponentFormModal";
import SWComponentModal from "../components/SWComponentModal";

export default function SWComponents({ user }) {
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("number");
  const [sortDir, setSortDir] = useState("asc");

  const roleCodes = user?.roles ?? [];
  const canManage = roleCodes.includes("PM") || roleCodes.includes("RQM");

  const loadComponents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

      const res = await fetchWithAuth(`/api/sw-components?${params.toString()}`);

      if (!res.ok) {
        console.error("Failed to load SW components");
        return;
      }

      setComponents(await res.json());
    } catch (err) {
      console.warn("loadSWComponents aborted:", err.message);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadComponents();
  }, [loadComponents]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const openComponent = async (id) => {
    const res = await fetchWithAuth(`/api/sw-components/${id}`);

    if (!res.ok) return;

    setSelectedComponent(await res.json());
    setShowModal(true);
  };

  const deleteComponent = async (id) => {
    if (!window.confirm("Delete this SW component?")) return;

    const res = await fetchWithAuth(`/api/sw-components/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Delete failed");
      return;
    }

    loadComponents();
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((current) => current === "asc" ? "desc" : "asc");
      return;
    }

    setSortField(field);
    setSortDir("asc");
  };

  const sortedComponents = [...components].sort((leftComponent, rightComponent) => {
    const direction = sortDir === "asc" ? 1 : -1;
    const valueA = leftComponent[sortField] ?? "";
    const valueB = rightComponent[sortField] ?? "";

    const compare = String(valueA).localeCompare(String(valueB), undefined, {
      numeric: true,
      sensitivity: "base"
    });

    if (compare !== 0) return compare * direction;

    return String(leftComponent.number ?? "").localeCompare(
      String(rightComponent.number ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const renderSwRequirements = (component) => {
    const swRequirements = component.SWRequirements ?? [];

    if (!swRequirements.length) {
      return <span className="text-muted">-</span>;
    }

    return (
      <div className="d-flex flex-wrap gap-1">
        {swRequirements.map((requirement) => (
          <span
            key={requirement.id}
            className="badge text-bg-light border"
            title={requirement.name ?? ""}
          >
            {requirement.number}
          </span>
        ))}
      </div>
    );
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search SW components..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                }}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <span className="badge text-bg-secondary">
            Total: {components.length}
          </span>
        </div>

        {canManage && (
          <button
            className="btn btn-success"
            onClick={() => {
              setEditingComponent(null);
              setShowForm(true);
            }}
          >
            Add SW component
          </button>
        )}
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th
                style={{ ...stickyHeaderStyle, width: "180px", cursor: "pointer" }}
                onClick={() => handleSort("number")}
              >
                <span className="d-inline-flex align-items-center">
                  Number
                  {renderSortIndicator("number")}
                </span>
              </th>
              <th
                style={{ ...stickyHeaderStyle, minWidth: "280px", cursor: "pointer" }}
                onClick={() => handleSort("name")}
              >
                <span className="d-inline-flex align-items-center">
                  Name
                  {renderSortIndicator("name")}
                </span>
              </th>
              <th style={{ ...stickyHeaderStyle, minWidth: "320px" }}>SW Requirements</th>
              {canManage && <th style={{ ...stickyHeaderStyle, width: "120px" }}>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {sortedComponents.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 4 : 3} className="text-center text-muted py-4">
                  No SW components found.
                </td>
              </tr>
            ) : sortedComponents.map((component, index) => (
              <tr key={component.id} style={getRowStyle(index)}>
                <td>
                  <button
                    className="btn btn-link p-0 fw-bold text-decoration-none text-start"
                    onClick={() => openComponent(component.id)}
                  >
                    {renderHighlightedText(component.number)}
                  </button>
                </td>
                <td>{renderHighlightedText(component.name)}</td>
                <td>{renderSwRequirements(component)}</td>
                {canManage && (
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => {
                        setEditingComponent(component);
                        setShowForm(true);
                      }}
                    >
                      <FaEdit />
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteComponent(component.id)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SWComponentFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadComponents}
        component={editingComponent}
      />

      <SWComponentModal
        show={showModal}
        onClose={() => setShowModal(false)}
        component={selectedComponent}
      />
    </div>
  );
}
